import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  Building2, Lock, KeyRound, Mail, Phone, Globe, Clock, CheckCircle2, 
  AlertCircle, ShieldCheck, LogOut, Save, RefreshCw, Eye, EyeOff, 
  Stethoscope, Languages, MapPin
} from 'lucide-react';
import axios from 'axios';

import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import SEOHead from '../components/SEOHead';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const ORGAN_OPTIONS = [
  'Kidney', 'Liver', 'Heart', 'Lung', 'Pancreas',
  'Small Intestine', 'Cornea', 'Skin', 'Bone', 'Bone Marrow',
  'Blood Vessel', 'Heart Valve', 'Hand', 'Face', 'Other'
];

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const CenterAdminPortal = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const tokenFromUrl = searchParams.get('token');
  const centerIdFromUrl = searchParams.get('center_id');

  const [mode, setMode] = useState(tokenFromUrl ? 'set_password' : 'login'); // 'set_password' | 'login' | 'dashboard'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Set password form
  const [setPasswordForm, setSetPasswordForm] = useState({
    token: tokenFromUrl || '',
    password: '',
    confirmPassword: ''
  });

  // Login form
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Authenticated Center data
  const [center, setCenter] = useState(null);
  const [languagesList, setLanguagesList] = useState([]);
  const [inventory, setInventory] = useState({});

  useEffect(() => {
    fetchLanguages();
    const storedToken = localStorage.getItem('center_admin_token');
    if (storedToken && !tokenFromUrl) {
      fetchMyCenter(storedToken);
    }
  }, []);

  const fetchLanguages = async () => {
    try {
      const res = await axios.get(`${API}/languages`);
      setLanguagesList(res.data || []);
    } catch (e) {
      // Fallback
    }
  };

  const fetchMyCenter = async (authToken) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/center-admin/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCenter(res.data);
      setMode('dashboard');
      fetchInventory(res.data.id, authToken);
    } catch (error) {
      localStorage.removeItem('center_admin_token');
      setMode('login');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async (centerId, authToken) => {
    try {
      const res = await axios.get(`${API}/donation-centers/${centerId}/inventory`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const invMap = {};
      (res.data || []).forEach(item => {
        invMap[item.blood_type] = {
          units_available: item.units_available,
          units_critical: item.units_critical
        };
      });
      setInventory(invMap);
    } catch (e) {
      // Ignore
    }
  };

  // Handle Set Password (magic link)
  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!setPasswordForm.token.trim()) {
      toast({ title: 'Error', description: 'Setup token is required.', variant: 'destructive' });
      return;
    }
    if (setPasswordForm.password.length < 8) {
      toast({ title: 'Weak Password', description: 'Password must be at least 8 characters long.', variant: 'destructive' });
      return;
    }
    if (setPasswordForm.password !== setPasswordForm.confirmPassword) {
      toast({ title: 'Mismatch', description: 'Passwords do not match.', variant: 'destructive' });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API}/center-admin/set-password`, {
        token: setPasswordForm.token.trim(),
        password: setPasswordForm.password
      });

      localStorage.setItem('center_admin_token', res.data.access_token);
      setCenter(res.data.center);
      setMode('dashboard');
      toast({
        title: 'Password Set Successfully! 🎉',
        description: 'Welcome to your Center Admin Portal.',
      });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to set password. Link may be expired.';
      toast({ title: 'Setup Failed', description: detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email.trim() || !loginForm.password) {
      toast({ title: 'Required', description: 'Email and password are required.', variant: 'destructive' });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API}/center-admin/login`, {
        email: loginForm.email.trim(),
        password: loginForm.password
      });

      localStorage.setItem('center_admin_token', res.data.access_token);
      setCenter(res.data.center);
      setMode('dashboard');
      fetchInventory(res.data.center.id, res.data.access_token);
      toast({
        title: 'Welcome Back!',
        description: `Signed in as administrator for ${res.data.center.name}`,
      });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid email or password.';
      toast({ title: 'Sign In Failed', description: detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.removeItem('center_admin_token');
    setCenter(null);
    setMode('login');
    toast({ title: 'Signed Out', description: 'You have been signed out of the portal.' });
  };

  // Handle Save Center Changes
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('center_admin_token');
    if (!token || !center) return;

    try {
      setSaving(true);
      const payload = {
        phone: center.phone,
        email: center.email,
        website: center.website,
        hours: center.hours,
        walk_in: Boolean(center.walk_in),
        contact_person: center.contact_person,
        contact_email: center.contact_email,
        accepts_blood: Boolean(center.accepts_blood),
        accepts_organs: Boolean(center.accepts_organs),
        accepts_body: Boolean(center.accepts_body),
        does_blood_transfusion: Boolean(center.does_blood_transfusion),
        does_organ_transplant: Boolean(center.does_organ_transplant),
        organ_transplant_types: center.organ_transplant_types || [],
        languages_spoken: center.languages_spoken || []
      };

      const res = await axios.put(`${API}/center-admin/me`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCenter(res.data.center);
      toast({
        title: 'Changes Saved! ✅',
        description: 'Your facility records have been updated and are live on DonorsHouse.',
      });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error.response?.data?.detail || 'Failed to update center information.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleCenterOrgan = (organ) => {
    const curr = center.organ_transplant_types || [];
    const updated = curr.includes(organ) ? curr.filter(o => o !== organ) : [...curr, organ];
    setCenter({ ...center, organ_transplant_types: updated });
  };

  const toggleCenterLanguage = (code) => {
    const curr = center.languages_spoken || [];
    const updated = curr.includes(code) ? curr.filter(c => c !== code) : [...curr, code];
    setCenter({ ...center, languages_spoken: updated });
  };

  return (
    <>
      <SEOHead
        title="Facility Admin Portal | DonorsHouse"
        description="Self-service management portal for donation centers, blood banks, and transplant hospitals on DonorsHouse."
      />

      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-600 rounded-xl text-white shadow-sm">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Facility Admin Portal</h1>
                <p className="text-xs sm:text-sm text-gray-600">Self-service facility record and inventory management</p>
              </div>
            </div>

            <Link to="/donation-centers">
              <Button variant="outline" size="sm" className="text-xs border-gray-300">
                ← Back to Directory
              </Button>
            </Link>
          </div>

          {/* MODE 1: Set Password with Magic Link */}
          {mode === 'set_password' && (
            <div className="max-w-md mx-auto">
              <Card className="shadow-md border-gray-200 bg-white">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-2">
                    <KeyRound className="h-6 w-6 text-red-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">Set Your Facility Password</CardTitle>
                  <CardDescription className="text-xs text-gray-600">
                    Your donation center has been approved! Create a secure password to access your facility dashboard.
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <form onSubmit={handleSetPassword} className="space-y-4">
                    <div>
                      <Label htmlFor="sp-pass" className="text-xs font-semibold text-gray-700">New Password *</Label>
                      <div className="relative mt-1">
                        <Input
                          id="sp-pass"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="At least 8 characters..."
                          value={setPasswordForm.password}
                          onChange={e => setSetPasswordForm({...setPasswordForm, password: e.target.value})}
                          className="pr-10 border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">Must include uppercase, lowercase, and numbers.</p>
                    </div>

                    <div>
                      <Label htmlFor="sp-confirm" className="text-xs font-semibold text-gray-700">Confirm Password *</Label>
                      <Input
                        id="sp-confirm"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Re-type your password..."
                        value={setPasswordForm.confirmPassword}
                        onChange={e => setSetPasswordForm({...setPasswordForm, confirmPassword: e.target.value})}
                        className="mt-1 border-gray-300"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading} 
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 mt-2"
                    >
                      {loading ? 'Setting Password...' : 'Save Password & Access Dashboard'}
                    </Button>

                    <p className="text-center text-xs text-gray-500 pt-2">
                      Already have a password?{' '}
                      <button 
                        type="button" 
                        onClick={() => setMode('login')} 
                        className="text-red-600 font-semibold hover:underline"
                      >
                        Sign in here
                      </button>
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* MODE 2: Login */}
          {mode === 'login' && (
            <div className="max-w-md mx-auto">
              <Card className="shadow-md border-gray-200 bg-white">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-2">
                    <Lock className="h-6 w-6 text-red-600" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">Sign in to Facility Portal</CardTitle>
                  <CardDescription className="text-xs text-gray-600">
                    Enter the contact email and password registered with your facility.
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email" className="text-xs font-semibold text-gray-700">Official Contact Email *</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="contact@center.org"
                        value={loginForm.email}
                        onChange={e => setLoginForm({...loginForm, email: e.target.value})}
                        className="mt-1 border-gray-300"
                      />
                    </div>

                    <div>
                      <Label htmlFor="login-pass" className="text-xs font-semibold text-gray-700">Password *</Label>
                      <div className="relative mt-1">
                        <Input
                          id="login-pass"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Your portal password"
                          value={loginForm.password}
                          onChange={e => setLoginForm({...loginForm, password: e.target.value})}
                          className="pr-10 border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading} 
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 mt-2"
                    >
                      {loading ? 'Authenticating...' : 'Sign In to Dashboard'}
                    </Button>

                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 text-center text-xs text-gray-600 mt-4">
                      First time here? When your center was approved, an onboarding link was sent to your contact email.
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* MODE 3: Authenticated Dashboard */}
          {mode === 'dashboard' && center && (
            <div className="space-y-8">
              
              {/* Center Overview Header */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-extrabold text-gray-900">{center.name}</h2>
                    {center.verified && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                        <ShieldCheck className="h-3 w-3 mr-1" /> Verified
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-red-600" />
                    {center.address}, {center.city}, {center.state} {center.zip_code}, {center.country}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <Button 
                    onClick={handleLogout} 
                    variant="outline" 
                    size="sm" 
                    className="text-gray-600 hover:text-red-700 border-gray-300"
                  >
                    <LogOut className="h-4 w-4 mr-1.5" />
                    Sign Out
                  </Button>
                </div>
              </div>

              {/* Edit Details Form */}
              <form onSubmit={handleSaveChanges} className="space-y-6">
                
                {/* Panel 1: Acceptance & Services */}
                <Card className="shadow-xs border-gray-200 bg-white">
                  <CardHeader className="border-b pb-3">
                    <CardTitle className="text-lg font-bold text-gray-900">
                      Donation Acceptance &amp; Medical Capabilities
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-600">
                      Keep your accepted donation categories and transplant procedures up to date.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-6">
                    <div>
                      <Label className="text-xs font-bold uppercase text-gray-700">What does your center accept?</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                        <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${center.accepts_blood ? 'bg-red-50 border-red-300 text-red-900 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                          <Checkbox 
                            checked={center.accepts_blood} 
                            onCheckedChange={chk => setCenter({...center, accepts_blood: Boolean(chk)})}
                            className="mr-3"
                          />
                          <span>🩸 Blood Donations</span>
                        </label>

                        <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${center.accepts_organs ? 'bg-red-50 border-red-300 text-red-900 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                          <Checkbox 
                            checked={center.accepts_organs} 
                            onCheckedChange={chk => setCenter({...center, accepts_organs: Boolean(chk)})}
                            className="mr-3"
                          />
                          <span>🫀 Organ Donations</span>
                        </label>

                        <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${center.accepts_body ? 'bg-red-50 border-red-300 text-red-900 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                          <Checkbox 
                            checked={center.accepts_body} 
                            onCheckedChange={chk => setCenter({...center, accepts_body: Boolean(chk)})}
                            className="mr-3"
                          />
                          <span>👤 Whole Body</span>
                        </label>
                      </div>
                    </div>

                    <div className="border-t pt-4 space-y-3">
                      <Label className="text-xs font-bold uppercase text-gray-700">Transplant &amp; Transfusion Services</Label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="edit-transfusion"
                            checked={center.does_blood_transfusion}
                            onCheckedChange={chk => setCenter({...center, does_blood_transfusion: Boolean(chk)})}
                          />
                          <Label htmlFor="edit-transfusion" className="text-sm text-gray-800 cursor-pointer">
                            Performs Blood Transfusion Services
                          </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="edit-transplant"
                            checked={center.does_organ_transplant}
                            onCheckedChange={chk => setCenter({...center, does_organ_transplant: Boolean(chk)})}
                          />
                          <Label htmlFor="edit-transplant" className="text-sm text-gray-800 cursor-pointer">
                            Performs Organ / Tissue Transplant Surgeries
                          </Label>
                        </div>
                      </div>

                      {center.does_organ_transplant && (
                        <div className="bg-purple-50/70 p-4 rounded-xl border border-purple-200 mt-3 space-y-2">
                          <p className="text-xs font-bold text-purple-900">Select Transplants Performed at this Facility:</p>
                          <div className="flex flex-wrap gap-2">
                            {ORGAN_OPTIONS.map(org => {
                              const sel = (center.organ_transplant_types || []).includes(org);
                              return (
                                <button
                                  key={org}
                                  type="button"
                                  onClick={() => toggleCenterOrgan(org)}
                                  className={`text-xs px-3 py-1 rounded-full border transition flex items-center gap-1 ${sel ? 'bg-purple-700 text-white border-purple-700 font-semibold' : 'bg-white text-gray-700 border-gray-300'}`}
                                >
                                  {sel && <CheckCircle2 className="h-3 w-3" />}
                                  {org}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Panel 2: Operational Info & Languages */}
                <Card className="shadow-xs border-gray-200 bg-white">
                  <CardHeader className="border-b pb-3">
                    <CardTitle className="text-lg font-bold text-gray-900">
                      Operations, Contact &amp; Languages
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="ed-phone" className="text-xs font-medium text-gray-700">Facility Phone</Label>
                        <Input
                          id="ed-phone"
                          value={center.phone || ''}
                          onChange={e => setCenter({...center, phone: e.target.value})}
                          className="mt-1 border-gray-300"
                        />
                      </div>

                      <div>
                        <Label htmlFor="ed-hours" className="text-xs font-medium text-gray-700">Operating Hours</Label>
                        <Input
                          id="ed-hours"
                          value={center.hours || ''}
                          onChange={e => setCenter({...center, hours: e.target.value})}
                          className="mt-1 border-gray-300"
                        />
                      </div>

                      <div>
                        <Label htmlFor="ed-website" className="text-xs font-medium text-gray-700">Website</Label>
                        <Input
                          id="ed-website"
                          value={center.website || ''}
                          onChange={e => setCenter({...center, website: e.target.value})}
                          className="mt-1 border-gray-300"
                        />
                      </div>

                      <div>
                        <Label htmlFor="ed-contact-name" className="text-xs font-medium text-gray-700">Contact Person Name</Label>
                        <Input
                          id="ed-contact-name"
                          value={center.contact_person || ''}
                          onChange={e => setCenter({...center, contact_person: e.target.value})}
                          className="mt-1 border-gray-300"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 pt-1">
                      <Checkbox 
                        id="ed-walkin"
                        checked={center.walk_in}
                        onCheckedChange={chk => setCenter({...center, walk_in: Boolean(chk)})}
                      />
                      <Label htmlFor="ed-walkin" className="text-sm text-gray-800 cursor-pointer">
                        Accepts walk-in donors without appointment
                      </Label>
                    </div>

                    {/* Languages Spoken */}
                    <div className="border-t pt-4 space-y-2">
                      <Label className="text-xs font-bold uppercase text-gray-700">Languages Spoken by Staff</Label>
                      <div className="max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg border border-gray-200 flex flex-wrap gap-1.5">
                        {languagesList.map(lang => {
                          const isSel = (center.languages_spoken || []).includes(lang.code);
                          return (
                            <button
                              key={lang.code}
                              type="button"
                              onClick={() => toggleCenterLanguage(lang.code)}
                              className={`text-xs px-2.5 py-1 rounded-md border transition ${isSel ? 'bg-red-700 text-white border-red-700 font-medium' : 'bg-white text-gray-700 border-gray-200'}`}
                            >
                              {lang.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="bg-gray-50 rounded-b-xl border-t flex justify-end p-4">
                    <Button 
                      type="submit" 
                      disabled={saving} 
                      className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 shadow-sm"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving Changes...' : 'Save & Publish Changes'}
                    </Button>
                  </CardFooter>
                </Card>

              </form>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default CenterAdminPortal;
